import React, {useEffect, useState} from 'react';
import './Billing.css';
import {useSelector} from 'react-redux';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {API_URL, createAxiosInstance} from '../../api';
import SweetAlert from 'sweetalert2';
import config from 'config';
import successIcon from '../../assets/success-icon.svg';

import {Button} from '@mui/material';

const Billing = () => {
  const showAlert = ({
    title,
    text,
    icon,
    imageUrl,
    confirmButtonText = 'OK',
    confirmButtonColor = '#712d94',
    showCancelButton = false,
    cancelButtonText = 'Cancel',
  } = {}) =>
    SweetAlert.fire({
      title,
      text,
      icon,
      imageUrl,
      confirmButtonText,
      confirmButtonColor,
      showCancelButton,
      cancelButtonText,
    });
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { isGuest } = useSelector((state) => state.main);
  const { _id, email } = useSelector((state) => state.profile.user);

  const [isLive, setIsLive] = useState("");
  const [hasFetchedBilling, setHasFetchedBilling] = useState(false);
  const [nextBillingDate, setNextBillingDate] = useState("");
  const [membership, setMembership] = useState("");
  const [price, setPrice] = useState("");
  const [daysLeft, setDaysLeft] = useState("");
  const [description, setDescription] = useState("");
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(""); // New state for cancellation status
  const formattedBilling = nextBillingDate
    ? new Date(nextBillingDate * 1000).toLocaleDateString("en-UK", {
        weekday: "long", // Optional: day of the week (e.g., Monday)
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "N/A"; // If nextBillingDate is not available

  const userId = useSelector((state) => state.profile.user.user);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true); // Set loading state
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${API_URL}/api/billings/status/${userId}`
        );

        // Destructure response data
        const {
          status,
          nextBilling,
          membership,
          price,
          description,
          cancel_at_period_end,
          daysLeft,
        } = response.data;

        // Set subscription status
        if (status === "active") {
          setIsLive("active");
        } else if (status === "paused") {
          setIsLive("paused");
        } else if (status === "past_due") {
          setIsLive("past_due");
        } else if (status === "canceled") {
          setIsLive("canceled");
        } else {
          setIsLive("inactive");
        }

        // Set other states based on fetched data
        setNextBillingDate(nextBilling || "N/A");
        setPrice(price || "N/A");
        setDaysLeft(daysLeft || "N/A");
        setDescription(description || "N/A");
        setCancelAtPeriodEnd(!!cancel_at_period_end);

        // Determine membership based on price
        if (price === 2) {
          setMembership("Monthly Subscription");
        } else if (price === 2499) {
          setMembership("Yearly Subscription");
        } else {
          setMembership(membership || "N/A");
        }
      } catch (err) {
        console.error("Error fetching billing data:", err);
      } finally {
        setLoading(false); // Stop loading after fetching is complete
        setHasFetchedBilling(true);
      }
    };

    if (userId) {
      fetchBillingData(); // Call the function directly instead of using setTimeout
    }
  }, [userId]);

  // Determine access and visibility
  const hasProfile = !!_id;
  const hasPaid = ["active", "paused", "past_due", "canceled"].includes(isLive);

  useEffect(() => {
    if (!hasFetchedBilling) return;
    // Restrict access to My Plan page when profile not created or not paid
    if (location.pathname === "/billing" && (!hasProfile || !hasPaid)) {
      navigate("/dashboard");
    }
  }, [hasFetchedBilling, hasProfile, hasPaid, location.pathname, navigate]);

  const handlePauseSubscription = () => {
    showAlert({
      title: "Are you sure?",
      text: "This will pause your payment plan!",
      icon: "warning",
      iconColor: "#d32f2f",
      showCancelButton: true,
      confirmButtonText: "Yes, pause it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#712d94",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const axiosInstance = createAxiosInstance();
          await axiosInstance.post(`${API_URL}/api/billings/pause/${userId}`);
          showAlert({
            title: "Paused",
            text: "Your payment plan has been paused.",
            imageUrl: successIcon,
          }).then(() => {
            setIsLive("paused");
            window.location.reload(); // Reload the page after the alert is confirmed
          });
        } catch (error) {
          showAlert({
            title: "Error",
            text: "There was an issue pausing your payment plan.",
            icon: "error",
          });
        }
      }
    });
  };

  const handleResumeSubscription = () => {
    showAlert({
      title: "Are you sure?",
      text: "This will resume your payment plan!",
      icon: "warning",
      iconColor: "#d32f2f",
      showCancelButton: true,
      confirmButtonText: "Yes, resume it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#712d94",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const axiosInstance = createAxiosInstance();
          const response = await axiosInstance.post(
            `${API_URL}/api/billings/resumes/${userId}`
          );
          // If the response is successful, show success message
          showAlert({
            title: "Resumed",
            text: "Your payment plan has been resumed.",
            imageUrl: successIcon,
          }).then(() => {
            window.location.reload(); // Reload the page after the alert is confirmed
          });
        } catch (error) {
          // Log the error details to the console for debugging
          console.error("Error resuming subscription:", error);

          // Check if error response exists and handle accordingly
          if (error.response) {
            // If the error has a response (i.e., status code other than 2xx)
            showAlert({
              title: "Error",
              text: `There was an issue resuming your payment plan: ${
                error.response.data.message || error.message
              }`,
              icon: "error",
            });
          } else {
            // If the error does not have a response, display a generic error message
            showAlert({
              title: "Error",
              text: "There was an unexpected error while resuming your payment plan.",
              icon: "error",
            });
          }
        }
      }
    });
  };

  const handleCancelSubscription = () => {
    showAlert({
      title: "Are you sure?",
      text: "This will cancel your payment plan, and you will no longer be billed.",
      icon: "warning",
      iconColor: "#d32f2f",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#712d94",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const axiosInstance = createAxiosInstance();
          await axiosInstance.delete(
            `${API_URL}/api/billings/cancel/${userId}`
          );
          showAlert({
            title: "Canceled",
            text: "Your payment plan has been cancelled. Your profile will remain visible to students until the end of your current billing period. You can resubscribe anytime.",
            imageUrl: successIcon,
          }).then(() => {
            setCancelAtPeriodEnd(true); // Update the state if cancellation is confirmed
            // window.location.reload(); // Reload the page after the alert is confirmed
          });
        } catch (error) {
          showAlert({
            title: "Error",
            text: "There was an issue canceling your payment plan.",
            icon: "error",
          });
        }
      }
    });
  };

  return (
    <div className="billing-container">
      <div className="billing-menu">
        <div className="returnToDash">
          {" "}
          <Link to="/dashboard">
            <svg
              id="Layer_1"
              data-name="Layer 1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 122.88 108.06"
            >
              <title>back-arrow</title>
              <path d="M63.94,24.28a14.28,14.28,0,0,0-20.36-20L4.1,44.42a14.27,14.27,0,0,0,0,20l38.69,39.35a14.27,14.27,0,0,0,20.35-20L48.06,68.41l60.66-.29a14.27,14.27,0,1,0-.23-28.54l-59.85.28,15.3-15.58Z" />
            </svg>
            Back To Dashboard
          </Link>
        </div>
        {hasProfile && hasPaid && (
          <Link
            className={`tab${
              location.pathname === "/billing"
                ? " active " + config["class-name"]
                : ""
            }`}
            to="/billing"
          >
            My Plan
          </Link>
        )}
        <Link
          className={`tab${
            location.pathname === "/billing/history"
              ? " active " + config["class-name"]
              : ""
          }`}
          to="/billing/history"
        >
          History
        </Link>
        <Link
          className={`tab${
            location.pathname === "/billing/saved-cards"
              ? " active " + config["class-name"]
              : ""
          }`}
          to="/billing/saved-cards"
        >
          Saved Cards
        </Link>
        <Link
          className={`tab${
            location.pathname === "/billing/change-plan"
              ? " active " + config["class-name"]
              : ""
          }`}
          to="/billing/change-plan"
        >
          Change Plan
        </Link>
      </div>

      <div className="billing-content">
        {location.pathname === "/billing" && (
          <div className="my-plan-section">
            <h1 className="page-title">My Plan</h1>
            <div className="plan-status">
              <div>
                <div className="billing-info">
                  <h3>Subscription Status</h3>
                  <p
                    className={
                      cancelAtPeriodEnd
                        ? "canceled"
                        : isLive === "active"
                        ? "active"
                        : isLive === "paused"
                        ? "paused"
                        : isLive === "past_due"
                        ? "past-due"
                        : isLive === "canceled"
                        ? "canceled"
                        : "inactive"
                    }
                  >
                    Status:{" "}
                    {cancelAtPeriodEnd
                      ? "Cancelled"
                      : isLive === "paused"
                      ? "Paused"
                      : isLive === "past_due"
                      ? "Past Due"
                      : isLive === "canceled"
                      ? "Cancelled"
                      : isLive}
                  </p>
                  <p>Membership: {membership}</p>
                  <p>Price: £{price}</p>

                  {!cancelAtPeriodEnd && isLive === "active" && (
                    <p>Next Billing Date: {formattedBilling}</p>
                  )}

                  {cancelAtPeriodEnd && (
                      <p className="cancel-note">
                        Your payment plan has been cancelled. <br />
                        Your profile will remain visible until the end of your billing period. You can resubscribe anytime.
                      </p>
                    )}

                  {isLive === "paused" && daysLeft > 0 && (
                    <p>
                      Your payment plan is paused. <br /> {daysLeft} days
                      remaining in the billing cycle.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            {isLive === "active" && !cancelAtPeriodEnd && (
              <div className="action-buttons">
                <Button
                  variant="contained"
                  color="primary"
                  className="cancel-btn"
                  onClick={handleCancelSubscription}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="pause-btn"
                  onClick={handlePauseSubscription}
                >
                  Pause
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="link-btn"
                  onClick={() => navigate("/billing/change-plan")}
                >
                  Change Plan
                </Button>
              </div>
            )}

            {isLive === "paused" && (
              <div className="action-buttons">
                <Button
                  variant="contained"
                  color="primary"
                  className="cancel-btn"
                  onClick={handleCancelSubscription}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="resume-btn"
                  onClick={handleResumeSubscription}
                >
                  Resume
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="link-btn"
                  onClick={() => navigate("/billing/change-plan")}
                >
                  Change Plan
                </Button>
              </div>
            )}

            {isLive === "canceled" && (
              <div className="action-buttons">
                <Button
                  variant="contained"
                  color="primary"
                  className="link-btn"
                  onClick={() => navigate("/billing/change-plan")}
                >
                  Reactivate
                </Button>
              </div>
            )}

            {isLive === "past_due" && (
              <div className="action-buttons">
                <Button
                  variant="contained"
                  color="primary"
                  className="link-btn"
                  onClick={() => navigate("/billing/change-plan")}
                >
                  Reactivate
                </Button>
              </div>
            )}

            {isLive === "inactive" && (
              <div className="action-buttons">
                <Button
                  variant="contained"
                  color="primary"
                  className="link-btn"
                  onClick={() => navigate("/billing/change-plan")}
                >
                  Reactivate
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
